<script setup lang="ts">
import { QField } from 'quasar'
import { computed, nextTick, onMounted, ref, shallowRef, watch } from 'vue'
import { Color } from '@previo/codebooks'
import { $t } from '../../composables/i18n'
import type { PValidationRule } from '../../types/types'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTextDescription from '../typography/system/PTextDescription.vue'
import PContextMenu from '../context-menu/PContextMenu.vue'
import { PBtnTableSize } from '../btn-table/types'
import PBtnTable from '../btn-table/PBtnTable.vue'
import PItem from '../list/PItem.vue'
import PList from '../list/PList.vue'
import { type Placeholder, PWysiwygTextInputFieldFormat } from './types'
import PTextInputField from './PTextInputField.vue'
import InputFieldLabel from './components/LabelInputField.vue'

type Props = {
  placeholders: Placeholder[]
  label?: null | string
  tooltip?: null | string
  hint?: null | string
  placeholder?: null | string
  rules?: PValidationRule[]
  disable?: boolean
  required?: boolean
  autofocus?: boolean
  variableFormat?: PWysiwygTextInputFieldFormat
  disableDelimiterEnd?: boolean
  errorMessage?: null | string
  maxlength?: null | number
  contextMenuTitle?: null | string
  disableSearch?: boolean
  cy: string
}

const model = defineModel<string | null>({ default: null })

const props = withDefaults(defineProps<Props>(), {
  label: null,
  tooltip: null,
  disable: false,
  autofocus: false,
  required: false,
  variableFormat: PWysiwygTextInputFieldFormat.BRACKETS,
  disableDelimiterEnd: false,
  contextMenuTitle: null,
  disableSearch: false,
  rules() {
    return []
  },
})

type Emits = {
  (e: 'blur', evt: Event): void
  (e: 'click', evt: Event): void
  (e: 'keydown', evt: Event): void
}

const emit = defineEmits<Emits>()

const contentEditableRef = shallowRef<HTMLDivElement | null>(null)
const searchQuery = ref('')
const isOpen = ref<boolean>(false)
const isFocused = ref<boolean>(false)
const lastOffset = ref<number | null>(null)

const getSelectionOffset = () => {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0 || !contentEditableRef.value) return null

  const range = selection.getRangeAt(0)
  if (!contentEditableRef.value.contains(range.startContainer)) return null

  const preCaretRange = range.cloneRange()
  preCaretRange.selectNodeContents(contentEditableRef.value)
  preCaretRange.setEnd(range.startContainer, range.startOffset)

  return preCaretRange.toString().length
}

const setSelectionOffset = (offset: number) => {
  if (!contentEditableRef.value) return

  const selection = window.getSelection()
  if (!selection) return

  const range = document.createRange()
  range.setStart(contentEditableRef.value, 0)
  range.collapse(true)

  let currentOffset = 0
  const nodeStack: Node[] = [contentEditableRef.value]
  let found = false

  while (nodeStack.length > 0) {
    const node = nodeStack.pop()!

    if (node.nodeType === Node.TEXT_NODE) {
      const nextOffset = currentOffset + node.textContent!.length
      if (offset <= nextOffset) {
        range.setStart(node, offset - currentOffset)
        range.collapse(true)
        found = true
        break
      }
      currentOffset = nextOffset
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement
      if (element.classList.contains('p-wysiwyg-placeholder-chip')) {
        const nextOffset = currentOffset + element.innerText.length
        if (offset <= nextOffset) {
          range.setStartAfter(element)
          range.collapse(true)
          found = true
          break
        }
        currentOffset = nextOffset
      } else {
        for (let i = node.childNodes.length - 1; i >= 0; i--) {
          nodeStack.push(node.childNodes[i])
        }
      }
    }
  }

  if (!found) {
    range.selectNodeContents(contentEditableRef.value)
    range.collapse(false)
  }

  selection.removeAllRanges()
  selection.addRange(range)
}

const saveSelection = () => {
  lastOffset.value = getSelectionOffset()
}

const restoreSelection = () => {
  if (lastOffset.value !== null) {
    setSelectionOffset(lastOffset.value)
  }
}

const filteredPlaceholders = computed(() => {
  if (!searchQuery.value) {
    return props.placeholders
  }
  const search = searchQuery.value.toLowerCase()
  return props.placeholders.filter(
    (p) => p.key.toLowerCase().includes(search) || p.label.toLowerCase().includes(search),
  )
})

const onPlaceholderButtonClick = () => {
  saveSelection()
  isOpen.value = true
}

const escapeHtml = (unsafe: string) => {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

const syncModel = () => {
  if (!contentEditableRef.value) return

  let text = ''
  const nodes = contentEditableRef.value.childNodes
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement
      if (element.classList.contains('p-wysiwyg-placeholder-chip')) {
        switch (props.variableFormat) {
          case PWysiwygTextInputFieldFormat.BRACKETS:
            text += `[[${element.dataset.key}]]`
            break
          case PWysiwygTextInputFieldFormat.CURLY:
            text += `{${element.dataset.key}}`
            break
        }
      } else {
        text += element.innerText
      }
    }
  }

  model.value = text || null

  nextTick(() => {
    restoreSelection()
  })
}

const updateContentFromModel = () => {
  if (!contentEditableRef.value) return

  const text = model.value || ''
  let html = escapeHtml(text)

  props.placeholders.forEach((p) => {
    let placeholderText: string
    switch (props.variableFormat) {
      case PWysiwygTextInputFieldFormat.BRACKETS:
        placeholderText = `[[${escapeHtml(p.label)}]]`
        break
      case PWysiwygTextInputFieldFormat.CURLY:
        placeholderText = `${escapeHtml(p.label)}`
        break
    }
    const placeholderHtml = `<span class="p-wysiwyg-placeholder-chip" contenteditable="false" data-key="${escapeHtml(
      p.key,
    )}">${placeholderText}</span>`
    const escapedKey = escapeHtml(p.key)
    let regexPattern: string

    switch (props.variableFormat) {
      case PWysiwygTextInputFieldFormat.BRACKETS:
        regexPattern = `\\[\\[${escapedKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]\\]`
        break
      case PWysiwygTextInputFieldFormat.CURLY:
        regexPattern = `{${escapedKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}}`
        break
    }

    const regex = new RegExp(regexPattern, 'g')
    html = html.replace(regex, placeholderHtml)
  })

  if (contentEditableRef.value.innerHTML !== html) {
    contentEditableRef.value.innerHTML = html
  }
}

onMounted(() => {
  updateContentFromModel()
  if (props.autofocus && contentEditableRef.value) {
    contentEditableRef.value.focus()
  }
})

watch(model, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    model.value = model.value || ''
    updateContentFromModel()
  }
})

watch(
  () => props.placeholders,
  () => {
    updateContentFromModel()
  },
  { deep: true },
)

const onInput = () => {
  saveSelection()
  syncModel()
}

const onBlur = (e: Event) => {
  saveSelection()
  isFocused.value = false
  emit('blur', e)
}

const onControlClick = (e: Event) => {
  saveSelection()
  isFocused.value = true
  emit('click', e)
}

const onPlaceholderClick = (placeholder: Placeholder) => {
  if (!contentEditableRef.value) return

  restoreSelection()

  const selection = window.getSelection()
  let range: Range

  if (selection && selection.rangeCount > 0) {
    range = selection.getRangeAt(0)
    if (!contentEditableRef.value.contains(range.commonAncestorContainer)) {
      range = document.createRange()
      range.selectNodeContents(contentEditableRef.value)
      range.collapse(false)
    }
  } else {
    range = document.createRange()
    range.selectNodeContents(contentEditableRef.value)
    range.collapse(false)
  }

  const chip = document.createElement('span')
  chip.className = 'p-wysiwyg-placeholder-chip'
  chip.contentEditable = 'false'
  chip.dataset.key = placeholder.key
  switch (props.variableFormat) {
    case PWysiwygTextInputFieldFormat.BRACKETS:
      chip.innerText = `[[${placeholder.label}]]`
      break
    case PWysiwygTextInputFieldFormat.CURLY:
      chip.innerText = `{${placeholder.label}}`
      break
  }

  range.deleteContents()
  range.insertNode(chip)

  if (props.disableDelimiterEnd) {
    range.setStartAfter(chip)
  } else {
    const space = document.createTextNode('\u00A0')
    range.setStartAfter(chip)
    range.insertNode(space)
    range.setStartAfter(space)
  }

  range.collapse(true)

  if (selection) {
    selection.removeAllRanges()
    selection.addRange(range)
  }

  isOpen.value = false
  searchQuery.value = ''

  saveSelection()
  syncModel()
}
</script>

<template>
  <input-field-label :label="label" :is-required="required" :tooltip="tooltip" />

  <q-field
    v-model="model"
    class="p-wysiwyg-text-input-field"
    :rules="rules"
    :error-message="errorMessage ?? undefined"
    :bg-color="Color.WHITE"
    :disable="disable"
    :data-cy="cy"
    :maxlength="maxlength ?? undefined"
    outlined
    lazy-rules
    no-error-icon
    hide-bottom-space
    @blur="onBlur"
    @click="onControlClick"
    @keydown="(e: Event) => $emit('keydown', e)"
  >
    <template #control>
      <div
        ref="contentEditableRef"
        :contenteditable="!disable"
        class="p-wysiwyg-contenteditable p-full-width"
        :class="{ 'is-disabled': disable, 'is-focused': isFocused }"
        :data-placeholder="placeholder ?? undefined"
        @input="onInput"
        @focus="onControlClick"
        @blur="onBlur"
        @mouseup="saveSelection"
        @keyup="saveSelection"
      />
    </template>

    <template #append>
      <div class="p-flex p-items-center p-wysiwyg-append">
        <template v-if="placeholders.length > 0">
          <p-btn-table
            icon="brackets-curly"
            :color="Color.GREY_600"
            :cy="`${cy}-placeholder-button`"
            :size="PBtnTableSize.SMALL"
            @click="onPlaceholderButtonClick"
          />
          <p-space :mobile="PSpaceSize.XS" />

          <p-context-menu
            v-model="isOpen"
            cy="p-wysiwyg-input-placeholder-menu"
            :title="contextMenuTitle ?? $t('Placeholders')"
          >
            <p-text-input-field
              v-if="!disableSearch"
              v-model="searchQuery"
              end-icon="search"
              :cy="`placeholder-search-input-parameter-${cy}`"
            />
            <p-list>
              <p-item
                v-for="item in filteredPlaceholders"
                :key="item.key"
                :text="item.label"
                :cy="`placeholder-parameter-${item.key}`"
                @click="onPlaceholderClick(item)"
              />
              <p-item
                v-if="filteredPlaceholders.length === 0"
                class="text-grey"
                :text="$t('No matches found')"
                :cy="`not-items-found-${cy}`"
                @click.stop
              />
            </p-list>
          </p-context-menu>
        </template>

        <slot name="ds-append" />
      </div>
    </template>
  </q-field>
  <template v-if="hint">
    <p-space :mobile="PSpaceSize.XS" vertical />
    <p-text-description :text="hint" :color="Color.GREY_500" />
  </template>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/variables/ds.radius';
@import '../../assets/scss/shared/brand.module';

.p-wysiwyg-text-input-field {
  :deep(.q-field__control) {
    padding-right: 1px;
  }

  .p-wysiwyg-append {
    padding-right: 3px;
  }

  .p-wysiwyg-contenteditable {
    min-height: 24px;
    line-height: 24px;
    outline: none;
    word-break: break-word;
    white-space: pre-wrap;

    &.is-disabled {
      pointer-events: none;
      opacity: 0.6;
    }

    &:empty:not(.is-focused):before {
      content: attr(data-placeholder);
      color: getColor('grey500');
      pointer-events: none;
      display: block;
      line-height: 24px;
    }

    :deep(.p-wysiwyg-placeholder-chip) {
      display: inline-block;
      background-color: getColor('grey100');
      border: 1px solid getColor('grey400');
      border-radius: $radius-md-mobile;
      padding: 6px 4px;
      margin: 0 2px;
      font-weight: 400;
      color: getColor('black');
      user-select: none;
      vertical-align: baseline;
      line-height: 10px;
    }
  }
}
</style>
